import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Menu, User, MapPin, Phone, ShoppingBag, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { brand } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navbar = () => {
  const { openCart, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goCategories = () => { navigate("/"); setTimeout(() => document.getElementById("categories")?.scrollIntoView({ behavior: "smooth" }), 120); };

  const CartBtn = () => (
    <button onClick={openCart} className="relative p-2 text-gray-700 hover:text-primary transition-colors">
      <ShoppingBag className="w-5 h-5" />
      {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
    </button>
  );

  return (
    <>
      <div className="hidden md:flex items-center justify-between bg-primary text-primary-foreground text-xs px-6 lg:px-12 py-1.5">
        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Luxury Home Salon Services in Gurugram · {brand.hours}</div>
        <a href={`tel:${brand.phoneRaw}`} className="flex items-center gap-1.5 hover:text-accent transition-colors"><Phone className="w-3.5 h-3.5" /> {brand.phone}</a>
      </div>

      <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur shadow-md" : "bg-white"}`}>
        <nav className="flex items-center justify-between px-4 md:px-6 lg:px-12 h-[68px]">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 shrink-0"><img src={brand.logo} alt={brand.name} className="h-10 md:h-11 w-auto object-contain" /></button>

          <div className="hidden lg:flex items-center gap-8">
            <button onClick={() => navigate("/")} className="text-[15px] font-medium text-gray-700 hover:text-primary transition-colors">Home</button>
            {/* Services dropdown */}
            <div className="relative group">
              <button onClick={goCategories} className="flex items-center gap-1 text-[15px] font-medium text-gray-700 hover:text-primary transition-colors">Services <ChevronDown className="w-4 h-4" /></button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="bg-white rounded-xl shadow-xl ring-1 ring-black/5 p-2 w-56 grid gap-0.5">
                  {categories.map((c) => (
                    <button key={c.id} onClick={() => navigate(`/${c.slug || encodeURIComponent(c.name)}`)} className="text-left px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-secondary hover:text-primary transition-colors">{c.name}</button>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={() => navigate("/packages")} className="text-[15px] font-medium text-gray-700 hover:text-primary transition-colors">Packages & Deals</button>
            <button onClick={() => navigate("/offers")} className="text-[15px] font-medium text-gray-700 hover:text-primary transition-colors">Offers</button>
            <button onClick={() => navigate("/page/about-us")} className="text-[15px] font-medium text-gray-700 hover:text-primary transition-colors">About Us</button>
            <button onClick={() => navigate("/contact")} className="text-[15px] font-medium text-gray-700 hover:text-primary transition-colors">Contact Us</button>
            <button onClick={() => navigate("/blog")} className="text-[15px] font-medium text-gray-700 hover:text-primary transition-colors">Blog</button>
          </div>

          <div className="flex items-center gap-1 md:gap-2">
            <CartBtn />
            <button onClick={() => navigate("/account")} className="hidden md:inline-flex items-center gap-1.5 text-gray-700 hover:text-primary transition-colors text-sm font-medium px-2">
              {user?.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <User className="w-5 h-5" />} {user ? user.name.split(" ")[0] : "Login"}
            </button>
            <Button onClick={openCart} className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-5 h-9 text-sm font-semibold">Book Now</Button>

            <Sheet>
              <SheetTrigger asChild><button className="lg:hidden p-2 text-gray-700"><Menu className="w-6 h-6" /></button></SheetTrigger>
              <SheetContent side="right" className="w-[280px]">
                <img src={brand.logo} alt={brand.name} className="h-10 w-auto object-contain mb-6" />
                <div className="flex flex-col gap-1">
                  <button onClick={() => navigate("/")} className="text-left py-3 px-2 rounded-lg text-gray-700 font-medium hover:bg-secondary hover:text-primary">Home</button>
                  <button onClick={goCategories} className="text-left py-3 px-2 rounded-lg text-gray-700 font-medium hover:bg-secondary hover:text-primary">Services</button>
                  <button onClick={() => navigate("/packages")} className="text-left py-3 px-2 rounded-lg text-gray-700 font-medium hover:bg-secondary hover:text-primary">Packages & Deals</button>
                  <button onClick={() => navigate("/offers")} className="text-left py-3 px-2 rounded-lg text-gray-700 font-medium hover:bg-secondary hover:text-primary">Offers</button>
                  <button onClick={() => navigate("/page/about-us")} className="text-left py-3 px-2 rounded-lg text-gray-700 font-medium hover:bg-secondary hover:text-primary">About Us</button>
                  <button onClick={() => navigate("/contact")} className="text-left py-3 px-2 rounded-lg text-gray-700 font-medium hover:bg-secondary hover:text-primary">Contact Us</button>
                  <a href="/#beauticians" className="py-3 px-2 rounded-lg text-gray-700 font-medium hover:bg-secondary hover:text-primary">Beauticians</a>
                  <button onClick={() => navigate("/blog")} className="text-left py-3 px-2 rounded-lg text-gray-700 font-medium hover:bg-secondary hover:text-primary">Blog</button>
                  <button onClick={() => navigate("/account")} className="text-left py-3 px-2 rounded-lg text-gray-700 font-medium hover:bg-secondary flex items-center gap-2"><User className="w-4 h-4" /> {user ? "My Account" : "Login"}</button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </header>
    </>
  );
};

export default Navbar;
